/**
 * Per-person RESPONSIBILITY TAGS + role-based home routing
 * (Phase 1 · chat-first correction batch · item 4).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  OWNER-LOCKED MODEL (2026-05-18, this batch)
 * ─────────────────────────────────────────────────────────────────────────
 *  The shell is role-aware (roles.ts: Admin · Staff · Client · …). That is
 *  the LOGIN role and decides what a person *may* see. WHERE a person LANDS
 *  on sign-in is a finer thing: it is owner-chosen per PERSON via
 *  responsibility tags, and a tag can also be scoped to a specific project.
 *
 *  Real staff roster (owner-CORRECTED 2026-05-18, Round 2):
 *    · Kunjal · Sukant · Prashant  → 3 operations people (staff)
 *    · Khilendra                   → STAFF (NOT admin). Scope: leads,
 *                                    appointment booking, HR, payments,
 *                                    attendance, managing Rohit's calendar.
 *                                    He does NOT have admin rights.
 *    · Rohit                       → the admin (admin_oversight) — the only
 *                                    admin entry, so admin home routing is
 *                                    still exercised.
 *
 *  Lead-handling staff (Khilendra) → home = Lead Inbox (via `leads` tag).
 *  Operations staff               → home = their assigned projects / work.
 *
 *  This is PURE + dependency-light (no Firebase, no React) so the Node
 *  test runner exercises the resolver directly — the same Sprint-3/4
 *  discipline as leads.ts / ancillary.ts. It plugs into the EXISTING
 *  roles.ts seam (it does not replace the login-role model — it refines
 *  the landing surface beneath it).
 * ─────────────────────────────────────────────────────────────────────────
 */
import type { Role } from './roles';

/**
 * The owner-chosen responsibility tags. These are NOT login roles — a
 * person keeps their login role (e.g. `staff`) and additionally carries
 * one or more of these tags that decide their home + work focus. The set
 * is intentionally small and owner-meaningful (not a generic permission
 * matrix).
 */
export type ResponsibilityTag =
  | 'leads' //          handles the front door (Lead Inbox)
  | 'appointments' //   booking / managing Rohit's calendar
  | 'hr' //             HR duties
  | 'payments' //       payment handling (NOT an admin right — task scope)
  | 'attendance' //     attendance tracking
  | 'calendar' //       managing Rohit's calendar
  | 'operations' //     project delivery work (the 3 ops people)
  | 'admin_oversight'; //cross-project admin pulse (Rohit only)

export const ALL_RESPONSIBILITY_TAGS: ResponsibilityTag[] = [
  'leads',
  'appointments',
  'hr',
  'payments',
  'attendance',
  'calendar',
  'operations',
  'admin_oversight',
];

export const RESPONSIBILITY_TAG_LABEL: Record<ResponsibilityTag, string> = {
  leads: 'Leads / front door',
  appointments: 'Appointments & booking',
  hr: 'HR',
  payments: 'Payments',
  attendance: 'Attendance',
  calendar: "Rohit's calendar",
  operations: 'Operations / delivery',
  admin_oversight: 'Admin oversight',
};

/**
 * One roster entry. `tags` are the person's GLOBAL responsibilities;
 * `projectTags` optionally overrides/extends per project (owner said a
 * tag "can also vary per project"). The login `role` is unchanged — this
 * sits beside it.
 */
export interface RosterPerson {
  uid: string;
  name: string;
  /** Login role (roles.ts) — unchanged by this module. */
  role: Role;
  /** Global responsibility tags. */
  tags: ResponsibilityTag[];
  /** Optional per-project tag overrides: { [pid]: extra tags }. */
  projectTags?: Record<string, ResponsibilityTag[]>;
}

/**
 * The real owner-CORRECTED roster (Round 2, 2026-05-18). Khilendra =
 * STAFF login (NOT admin — no admin rights). His real scope = leads,
 * appointment booking, HR, payments, attendance, managing Rohit's
 * calendar — represented as responsibility tags. The 3 ops people =
 * staff login + operations. Rohit is the ONLY admin (admin_oversight) so
 * the admin home routing is still exercised.
 */
export const ROSTER: RosterPerson[] = [
  {
    uid: 'u-khilendra',
    name: 'Khilendra',
    role: 'staff',
    tags: [
      'leads',
      'appointments',
      'hr',
      'payments',
      'attendance',
      'calendar',
    ],
  },
  {
    uid: 'u-kunjal',
    name: 'Kunjal',
    role: 'staff',
    tags: ['operations'],
  },
  {
    uid: 'u-sukant',
    name: 'Sukant',
    role: 'staff',
    tags: ['operations'],
  },
  {
    uid: 'u-prashant',
    name: 'Prashant',
    role: 'staff',
    tags: ['operations'],
  },
  {
    uid: 'u-rohit',
    name: 'Rohit',
    role: 'admin',
    tags: ['admin_oversight'],
  },
];

/**
 * The demo signed-in person (mirrors trackDemo.DEMO_STAFF.uid so the
 * existing demo identity is unchanged): Khilendra. Kept here so Home can
 * resolve the landing surface without importing the trackDemo seam.
 */
export const DEMO_STAFF = { uid: 'u-khilendra', name: 'Khilendra' };

export function findPerson(uid: string | null | undefined): RosterPerson | null {
  if (!uid) return null;
  return ROSTER.find((p) => p.uid === uid) ?? null;
}

/**
 * Effective responsibility tags for a person, optionally within a
 * project. Per-project tags are UNIONed with the global tags (a project
 * can grant extra responsibility; it never silently removes the person's
 * baseline). Pure + deterministic.
 */
export function effectiveTags(
  person: RosterPerson | null,
  pid?: string | null,
): ResponsibilityTag[] {
  if (!person) return [];
  const base = new Set<ResponsibilityTag>(person.tags);
  if (pid && person.projectTags && person.projectTags[pid]) {
    for (const t of person.projectTags[pid]) base.add(t);
  }
  return [...base];
}

/** Does this person carry the given responsibility (globally or on pid)? */
export function hasResponsibility(
  person: RosterPerson | null,
  tag: ResponsibilityTag,
  pid?: string | null,
): boolean {
  return effectiveTags(person, pid).includes(tag);
}

/**
 * THE home-route resolver. Owner-locked priority:
 *   1. carries `leads`       → Lead Inbox  (Khilendra)
 *   2. carries `operations`  → their work  (the 3 ops people)
 *   3. carries `admin_oversight` (and not leads) → admin pulse
 *   4. anything else         → the role's default home ('/')
 *
 * Returns a route path AND a `home` enum the Home screen switches on so
 * the same person can be sent to the right *track view* within '/'. This
 * never overrides the login-role GATE (roles.ts still decides access) —
 * it only chooses the landing surface a permitted person sees first.
 */
export type HomeKind = 'leads' | 'ops_work' | 'admin_pulse' | 'role_default';

export interface HomeResolution {
  /** Route to land on after sign-in. */
  route: string;
  /** Which home view variant to render at that route. */
  home: HomeKind;
  /** Plain-language why (shown as a subtle note — owner can sanity-check). */
  reason: string;
}

export function resolveHome(
  person: RosterPerson | null,
  loginRole: Role | null | undefined,
): HomeResolution {
  const tags = effectiveTags(person);

  if (tags.includes('leads')) {
    return {
      route: '/leads',
      home: 'leads',
      reason: `${person?.name ?? 'This person'} handles the front door — home is the Lead Inbox.`,
    };
  }
  if (tags.includes('operations')) {
    return {
      route: '/',
      home: 'ops_work',
      reason: `${person?.name ?? 'This person'} is operations — home is their assigned projects / work.`,
    };
  }
  if (tags.includes('admin_oversight')) {
    return {
      route: '/',
      home: 'admin_pulse',
      reason: `${person?.name ?? 'This person'} has admin oversight — home is the cross-project pulse.`,
    };
  }
  // Unknown person / no tags → fall back to the login-role default home.
  return {
    route: '/',
    home: 'role_default',
    reason: loginRole
      ? `No responsibility tag set — falling back to the ${loginRole} default home.`
      : 'No responsibility tag set — default home.',
  };
}
